// src/pages/preventivas/PreventivaExecutarPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { pode } from "../../utils/permissoes";
import { useIsMobile } from "../../hooks/useIsMobile";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";

import { db } from "../../config/firebase";
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  writeBatch,} from "firebase/firestore";

import { formatData } from "../../utils/dateFormat";
import {
  calcularCamadaExecutar,
  calcularProximaDataCamadas,
  filtrarItensPorCamada,
  formatarCamadaDetalhe,
  montarAtualizacaoCamadas,
} from "../../utils/preventivaCamadas";
import { uploadToStorage } from "../../utils/storageUpload";

function normalizarTipo(tipo) {
  const t = String(tipo || "").toUpperCase().trim();
  if (t === "SIM_NAO") return "SIM_NAO";
  if (t === "OK_NOK") return "OK_NOK";
  if (t === "BAR") return "BAR";
  if (t === "TEXTO") return "TEXTO";
  return "SIM_NAO";
}

function PreventivaExecutarPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const podeIniciarExecucao = pode(usuarioLogado, "preventivas", "execucao", "iniciarExecucao");
  const isMobile = useIsMobile(900);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Quando esta página é aberta a partir de "Meus atendimentos",
  // queremos voltar para lá (sem quebrar os outros fluxos).
  const voltarPara = location?.state?.voltarPara;
  const navegarVoltar = () => navigate(voltarPara || "/preventivas/abertas");

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  const [preventiva, setPreventiva] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [observacoes, setObservacoes] = useState("");
  const [fotos, setFotos] = useState([]);
  const [uploadandoFotos, setUploadandoFotos] = useState(false);

  
  if (!podeIniciarExecucao) {
    return (
      <div style={{ padding: 16 }}>
        <h3>Acesso restrito</h3>
        <p>Você não tem permissão para iniciar execução de preventiva.</p>
      </div>
    );
  }

useEffect(() => {
    async function carregar() {
      if (!id) return;
      setCarregando(true);
      setErro("");
      setSucesso("");

      try {
        const ref = doc(db, "preventivas", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setErro("Preventiva não encontrada.");
          setPreventiva(null);
          return;
        }

        const data = { id: snap.id, ...snap.data() };
        setPreventiva(data);
      } catch (e) {
        console.error("Erro ao carregar preventiva:", e);
        setErro("Erro ao carregar preventiva. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregar();
  }, [id]);

  const competenciaExecucao = location?.state?.competencia || null;

  const referenciaExecucao = useMemo(() => {
    const c = String(competenciaExecucao || "").trim();
    const m = c.match(/^(\d{4})-(\d{2})(?:-(Q1|Q2))?$/);
    if (!m) return new Date();

    const ano = Number(m[1]);
    const mes = Number(m[2]) - 1;
    const dia = m[3] === "Q2" ? 15 : 1;

    // 12:00 para evitar viradas de data por fuso/DST em alguns ambientes.
    return new Date(ano, mes, dia, 12, 0, 0, 0);
  }, [preventiva?.id, competenciaExecucao]);

  const camadaExecutar = useMemo(() => {
    const camadaPrevista = String(
      preventiva?.camadaPrevista || preventiva?.periodicidade || ""
    ).trim().toUpperCase();

    if (["QUINZENAL", "MENSAL", "TRIMESTRAL", "SEMESTRAL"].includes(camadaPrevista)) {
      return camadaPrevista;
    }

    return calcularCamadaExecutar(preventiva, referenciaExecucao);
  }, [preventiva, referenciaExecucao]);

  const itensComIndice = useMemo(() => {
    const base = Array.isArray(preventiva?.itens) ? preventiva.itens : [];
    return base.map((item, idx) => ({ ...item, indexOriginal: idx }));
  }, [preventiva]);

  const itensFiltrados = useMemo(() => {
    return filtrarItensPorCamada(itensComIndice, camadaExecutar);
  }, [itensComIndice, camadaExecutar]);

  useEffect(() => {
    setRespostas(
      itensFiltrados.map((it) => {
        const tipo = normalizarTipo(it.tipoResposta);
        const valorInicial = tipo === "BAR" ? "" : "";
        return { valor: valorInicial };
      })
    );
  }, [preventiva?.id, itensFiltrados]);

  const titulo = preventiva?.nome || "Executar Preventiva";
  const subtitulo = "Preventivas / Executar";
  const camadaLabel = camadaExecutar ? formatarCamadaDetalhe(camadaExecutar) : "Sem itens vencidos";

  function setResposta(idx, valor) {
    setRespostas((prev) => {
      const novo = [...prev];
      novo[idx] = { ...(novo[idx] || {}), valor };
      return novo;
    });
  }

  async function handleAdicionarFotos(event) {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const tiposFotoPermitidos = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    setUploadandoFotos(true);
    const novasFotos = [];
    setErro("");
    setSucesso("");

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Valida se é imagem
        if (!file.type.startsWith("image/")) {
          console.warn(`Arquivo ${file.name} não é uma imagem, pulando...`);
          continue;
        }

        // Fotos aceitas: JPG/JPEG/PNG/WEBP (SVG costuma ser bloqueado pelas regras do Storage)
        if (!tiposFotoPermitidos.includes(String(file.type || "").toLowerCase())) {
          setErro(`Formato não suportado para ${file.name}. Use JPG, PNG ou WEBP.`);
          continue;
        }

        // Valida tamanho (máximo 10MB)
        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
          setErro(`Arquivo ${file.name} é muito grande (máximo 10MB)`);
          continue;
        }

        try {
          // Upload para Firebase Storage
          const uploadBase = {
            grupoId: usuarioLogado.grupoId,
            empresaId: preventiva?.empresaId || null,
            file,
            filenamePrefix: `preventiva_${preventiva?.id}_${Date.now()}`,
          };

          const url = await uploadToStorage({
            ...uploadBase,
            pasta: "preventivas_fotos",
          });

          novasFotos.push({
            url,
            nome: file.name,
            uploadEm: new Date().toISOString(),
          });
        } catch (uploadErr) {
          console.error(`Erro ao fazer upload de ${file.name}:`, uploadErr);
          setErro(`Erro ao fazer upload de ${file.name}: ${uploadErr.message || uploadErr}`);
          throw uploadErr;
        }
      }

      setFotos((prev) => [...prev, ...novasFotos]);
      if (novasFotos.length > 0) {
        setSucesso(`${novasFotos.length} foto(s) adicionada(s) com sucesso!`);
      }
    } catch (err) {
      console.error("Erro ao fazer upload de fotos:", err);
      if (!erro) {
        setErro(`Erro ao fazer upload das fotos: ${err.message || String(err)}`);
      }
    } finally {
      setUploadandoFotos(false);
      // Limpa o input
      event.target.value = "";
    }
  }

  function removerFoto(idx) {
    setFotos((prev) => prev.filter((_, i) => i !== idx));
  }

  function validar() {
    // Regra simples: itens com pergunta preenchida precisam de resposta
    for (let i = 0; i < itensFiltrados.length; i++) {
      const it = itensFiltrados[i] || {};
      const pergunta = String(it.pergunta || "").trim();
      if (!pergunta) continue;

      const tipo = normalizarTipo(it.tipoResposta);
      const valor = respostas?.[i]?.valor;

      if (tipo === "BAR") {
        const n = Number(valor);
        if (valor === "" || Number.isNaN(n)) {
          return `Responda o item ${i + 1} (BAR).`;
        }
      } else {
        if (valor == null || String(valor).trim() === "") {
          return `Responda o item ${i + 1}.`;
        }
      }
    }
    return "";
  }

  async function salvarExecucao() {
    setErro("");
    setSucesso("");

    if (!camadaExecutar) {
      setErro("Visita antecipada: nenhum item venceu ainda.");
      return;
    }

    if (!usuarioLogado?.grupoId) {
      setErro("Usuário logado não possui grupo definido.");
      return;
    }
    if (!preventiva?.id) {
      setErro("Preventiva inválida.");
      return;
    }

    const msg = validar();
    if (msg) {
      setErro(msg);
      return;
    }

    try {
      setSalvando(true);
      const agora = new Date();

      // Data de ciclo (competência/quinzena planejada).
      // Evita drift: se o técnico executar fora do dia planejado, a sequência QUINZENAL (Q1/Q2) continua correta.
      const dataCiclo = referenciaExecucao || agora;

      const payloadRespostas = itensFiltrados.map((it, idx) => {
        const tipo = normalizarTipo(it.tipoResposta);
        const pergunta = String(it.pergunta || "").trim();
        let valor = respostas?.[idx]?.valor;

        if (tipo === "BAR") {
          const n = Number(valor);
          valor = Number.isNaN(n) ? null : n;
        } else {
          valor = valor == null ? null : String(valor);
        }

        return {
          index: it?.indexOriginal ?? idx,
          pergunta,
          periodicidade: it?.periodicidade || null,
          tipoResposta: tipo,
          valor,
        };
      });

      // 1) registra execução + 2) atualiza a preventiva (commit atômico)
      const execRef = doc(collection(db, "preventivasExecucoes"));
      const batch = writeBatch(db);

      batch.set(execRef, {
        grupoId: usuarioLogado.grupoId,
        empresaId: preventiva.empresaId || null,
        empresaNome: preventiva.empresaNome || null,
        maquinaId: preventiva.maquinaId || null,
        maquinaNome: preventiva.maquinaNome || null,

        preventivaId: preventiva.id,
        preventivaCodigo: preventiva.codigo || null,
        preventivaNome: preventiva.nome || null,
        periodicidade: preventiva.periodicidade || null,
        camadaExecutada: camadaExecutar,

        competencia: competenciaExecucao || null,
        referenciaCiclo: referenciaExecucao || null,

        dataExecucao: agora,
        respostas: payloadRespostas,
        observacoes: observacoes.trim() || null,
        fotos: fotos.map((f) => ({ url: f.url, nome: f.nome, uploadEm: f.uploadEm })) || [],

        executanteId:
          usuarioLogado.id || usuarioLogado.uid || usuarioLogado.userId || null,
        executanteNome:
          usuarioLogado.nome ||
          usuarioLogado.displayName ||
          usuarioLogado.email ||
          null,

        criadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Atualiza preventiva: ultimaExecucao / camadas / proximaData
      const contratoTipo =
        preventiva?.contratoTipo ||
        preventiva?.setorContratoTipo ||
        preventiva?.tipoContrato ||
        "";
      const atualizacaoCamadas = montarAtualizacaoCamadas(
        camadaExecutar,
        dataCiclo,
        contratoTipo
      );
      const proxima = calcularProximaDataCamadas(
        { ...preventiva, ...atualizacaoCamadas, contratoTipo },
        dataCiclo
      );
      const upd = {
        ultimaExecucao: agora,
        ultimaExecucaoCiclo: dataCiclo,
        ...atualizacaoCamadas,
        updatedAt: serverTimestamp(),
        contratoTipo: contratoTipo || null,
        camadaForcada: null,
      };
      if (proxima) {
        upd.proximaData = proxima;
      }
      batch.update(doc(db, "preventivas", preventiva.id), upd);

      await batch.commit();

      setSucesso("Execução registrada com sucesso!");

      // volta para lista (curto delay para o usuário ver o ok)
      setTimeout(() => {
        navegarVoltar();
      }, 700);
    } catch (e) {
      console.error("Erro ao salvar execução:", e);
      setErro("Erro ao registrar execução. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  }

  const content = (
    <section className="pc-card" style={{ maxWidth: 980, margin: "0 auto" }}>
      <header style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h3 style={{ margin: 0 }}>{titulo}</h3>
          <p style={{ marginTop: 6, opacity: 0.85 }}>
            {preventiva?.codigo ? `Código: ${preventiva.codigo} · ` : ""}
            {preventiva?.empresaNome ? `Empresa: ${preventiva.empresaNome} · ` : ""}
            {preventiva?.maquinaNome ? `Máquina: ${preventiva.maquinaNome} · ` : ""}
            Próxima execução: {formatData(preventiva?.proximaData)}
            {camadaLabel ? ` · Camada: ${camadaLabel}` : ""}
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="pc-btn-secundario"
            onClick={navegarVoltar}
            disabled={salvando}
          >
            Voltar
          </button>
        </div>
      </header>

      {erro && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: "#7f1d1d",
            color: "#fecaca",
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {sucesso && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 10,
            background: "#14532d",
            color: "#bbf7d0",
            fontSize: 14,
          }}
        >
          {sucesso}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        {carregando ? (
          <p style={{ padding: 10 }}>Carregando formulário...</p>
        ) : !preventiva ? (
          <p style={{ padding: 10 }}>Preventiva não encontrada.</p>
        ) : !camadaExecutar ? (
          <p style={{ padding: 10 }}>Visita antecipada - nenhum item venceu para hoje.</p>
        ) : itensFiltrados.length === 0 ? (
          <p style={{ padding: 10 }}>Nenhum item desta camada foi cadastrado.</p>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {itensFiltrados.map((it, idx) => {
              const tipo = normalizarTipo(it.tipoResposta);
              const pergunta = String(it.pergunta || "").trim() || `Item ${idx + 1}`;
              const valor = respostas?.[idx]?.valor ?? "";

              return (
                <div
                  key={idx}
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    border: "1px solid rgba(148,163,184,0.35)",
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <strong>{idx + 1}. {pergunta}</strong>
</div>

                  {it.fotoUrl ? (
                    <div style={{ marginTop: 10 }}>
                      <img
                        src={it.fotoUrl}
                        alt={`Foto do item ${idx + 1}`}
                        style={{
                          width: "100%",
                          maxHeight: 220,
                          objectFit: "contain",
                          borderRadius: 10,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "#0f172a",
                          padding: 6,
                        }}
                      />
                    </div>
                  ) : null}

                  <div style={{ marginTop: 10 }}>
                    {tipo === "SIM_NAO" && (
                      <select
                        value={valor}
                        onChange={(e) => setResposta(idx, e.target.value)}
                        className="pc-input"
                        style={{ width: "100%" }}                      >
                        <option value="">Selecione...</option>
                        <option value="SIM">Sim</option>
                        <option value="NAO">Não</option>
                      </select>
                    )}

                    {tipo === "OK_NOK" && (
                      <select
                        value={valor}
                        onChange={(e) => setResposta(idx, e.target.value)}
                        className="pc-input"
                        style={{ width: "100%" }}                      >
                        <option value="">Selecione...</option>
                        <option value="OK">OK</option>
                        <option value="NOK">NOK</option>
                      </select>
                    )}

                    {tipo === "BAR" && (
                      <div style={{ display: "grid", gap: 8 }}>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={valor === "" ? 0 : Number(valor)}
                          onChange={(e) => setResposta(idx, e.target.value)}
                          style={{ width: "100%" }}
                        />
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={valor}
                          onChange={(e) => setResposta(idx, e.target.value)}
                          className="pc-input"
                          style={{ width: "100%" }}
                          placeholder="0-100"
                        />
                      </div>
                    )}

                    {tipo === "TEXTO" && (
                      <textarea
                        rows={3}
                        value={valor}
                        onChange={(e) => setResposta(idx, e.target.value)}
                        className="pc-input"
                        style={{ width: "100%" }}
                                                placeholder="Digite..."
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Observações (opcional)
          </label>
          <textarea
            rows={3}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            className="pc-input"
                        style={{ width: "100%" }}
                                    placeholder="Ex.: anomalias, ajustes, materiais usados..."
          />
        </div>

        <div style={{ marginTop: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Fotos (opcional)
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            {/* Upload da galeria */}
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "2px dashed rgba(148,163,184,0.5)",
                background: "rgba(148,163,184,0.08)",
                textAlign: "center",
                cursor: uploadandoFotos ? "not-allowed" : "pointer",
                opacity: uploadandoFotos ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                multiple
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleAdicionarFotos}
                disabled={uploadandoFotos}
                style={{ display: "none" }}
                id="input-fotos-galeria"
              />
              <label
                htmlFor="input-fotos-galeria"
                style={{ cursor: uploadandoFotos ? "not-allowed" : "pointer", display: "block" }}
              >
                {uploadandoFotos ? (
                  <span>Enviando...</span>
                ) : (
                  <span>
                    🖼️ Galeria
                    <br />
                    <small>Selecionar fotos</small>
                  </span>
                )}
              </label>
            </div>

            {/* Captura de camera */}
            <div
              style={{
                padding: 12,
                borderRadius: 8,
                border: "2px dashed rgba(148,163,184,0.5)",
                background: "rgba(148,163,184,0.08)",
                textAlign: "center",
                cursor: uploadandoFotos ? "not-allowed" : "pointer",
                opacity: uploadandoFotos ? 0.6 : 1,
              }}
            >
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                capture="environment"
                onChange={handleAdicionarFotos}
                disabled={uploadandoFotos}
                style={{ display: "none" }}
                id="input-fotos-camera"
              />
              <label
                htmlFor="input-fotos-camera"
                style={{ cursor: uploadandoFotos ? "not-allowed" : "pointer", display: "block" }}
              >
                {uploadandoFotos ? (
                  <span>Enviando...</span>
                ) : (
                  <span>
                    📸 Câmera
                    <br />
                    <small>Tirar foto</small>
                  </span>
                )}
              </label>
            </div>
          </div>

          {fotos.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>
                {fotos.length} foto(s) adicionada(s)
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                  gap: 8,
                }}
              >
                {fotos.map((foto, idx) => (
                  <div
                    key={idx}
                    style={{
                      position: "relative",
                      borderRadius: 8,
                      overflow: "hidden",
                      border: "1px solid rgba(148,163,184,0.35)",
                    }}
                  >
                    <img
                      src={foto.url}
                      alt={`Foto ${idx + 1}`}
                      style={{
                        width: "100%",
                        height: 100,
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => removerFoto(idx)}
                      style={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "rgba(239, 68, 68, 0.9)",
                        border: "none",
                        color: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                      }}
                      title="Remover foto"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            className={isMobile ? "m-btn-primary" : "pc-btn-primario"}
            onClick={salvarExecucao}
            disabled={salvando || carregando || !preventiva || !camadaExecutar}
            style={{ minWidth: 180 }}
          >
            {salvando ? "Salvando..." : "Salvar execução"}
          </button>
        </div>
      </div>
    </section>
  );

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Executar Preventiva"
        sidebarAberta={false}
        onToggleSidebar={() => {}}
      >
        {content}
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Executar Preventiva" subtitulo={subtitulo}>
      {content}
    </LayoutDesktop>
  );
}

export default PreventivaExecutarPage;